import { UserPreferences } from "@/types";

// Currency symbols map - centralized for consistency
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'CHF',
  HKD: 'HK$',
  SGD: 'S$',
  SEK: 'kr',
  KRW: '₩',
  NOK: 'kr',
  NZD: 'NZ$',
  MXN: 'MX$',
  ZAR: 'R',
  BRL: 'R$',
  RUB: '₽',
  TRY: '₺',
  PLN: 'zł',
  THB: '฿',
  IDR: 'Rp',
  MYR: 'RM',
  PHP: '₱',
  VND: '₫',
  AED: 'د.إ',
  SAR: '﷼',
  QAR: '﷼',
  KWD: 'د.ك',
  BHD: '.د.ب',
  OMR: '﷼',
  EGP: 'E£',
  PKR: 'Rs',
  BDT: '৳',
  LKR: 'Rs',
  NPR: 'Rs',
  NGN: '₦',
  KES: 'KSh',
  GHS: '₵',
  MAD: 'د.م.',
  TND: 'د.ت',
  ILS: '₪',
  JOD: 'د.ا',
  LBP: 'ل.ل',
  DZD: 'د.ج',
  IQD: 'ع.د',
  IRR: '﷼',
  AFN: '؋',
  MMK: 'K',
  CLP: 'CLP$',
  COP: 'COL$',
  PEN: 'S/',
  ARS: 'AR$',
  VES: 'Bs.',
  UAH: '₴',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  RSD: 'дин.',
  ISK: 'kr',
  DKK: 'kr',
  TWD: 'NT$',
};

// Default currency changed to USD
const DEFAULT_CURRENCY = 'USD';

// Helper to get symbol from code
export const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

export const formatNumber = (
  amount: number,
  format: UserPreferences['number_format'] = 'comma'
): string => {
  const absAmount = Math.abs(amount);
  
  switch (format) {
    case 'dot':
      // European format: 1.234,56
      return absAmount.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'space':
      // International format: 1 234.56
      return absAmount.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'comma':
    default:
      // Standard format: 1,234.56
      return absAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  }
};

export const formatDate = (
  date: Date,
  format: UserPreferences['date_format'] = 'DD/MM/YYYY'
): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();

  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${day}/${month}/${year}`;
  }
};

export const formatCurrency = (
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  numberFormat: UserPreferences['number_format'] = 'comma'
): string => {
  const formattedNumber = formatNumber(amount, numberFormat);
  const symbol = getCurrencySymbol(currency);
  return `${symbol} ${formattedNumber}`;
};